<template>
    <VSelect
        ref="select"
        :value="value"
        :options="options"
        :filterable="false"
        :clearable="clearable"
        :append-to-body="true"
        :disabled="disabled || !lookup.available"
        :placeholder="placeholder"
        label="displayLabel"
        @input="$emit('input', $event)"
        @search="search"
        @open="options = []"
    >
        <template #option="option">
            <div>
                <div>{{ option.name || option.identifier || productIdLabel(option) }}</div>
                <small v-if="option.identifier" class="text-muted">
                    {{ option.identifier }}
                </small>
            </div>
        </template>
        <template #selected-option="option">
            <span>{{ option.displayLabel || displayLabel(option) }}</span>
        </template>
        <template #no-options="slotProps">
            <div class="text-muted small">
                {{ noOptionsLabel(slotProps && slotProps.search) }}
            </div>
        </template>
    </VSelect>
</template>

<script>
import VSelect from 'vue-select'

const productId = product => product && (product.productId || product.id)

export default {
    name: 'ProductLookupSelect',
    components: { VSelect },
    props: {
        value: { type: Object, default: null },
        lookup: { type: Object, required: true },
        excludedProductIds: { type: Array, default: () => [] },
        clearable: { type: Boolean, default: true },
        disabled: { type: Boolean, default: false },
        placeholder: { type: String, default: '' },
        minChars: { type: Number, default: 2 },
        debounceMs: { type: Number, default: 400 },
    },
    data() {
        return {
            options: [],
            searchTimer: null,
            searchRevision: 0,
        }
    },
    beforeDestroy() {
        clearTimeout(this.searchTimer)
        this.searchRevision += 1
    },
    methods: {
        focusSearchInput() {
            const { select } = this.$refs
            if (!select) {
                return
            }
            if (typeof select.focus === 'function') {
                select.focus()
            }
            if (select.searchEl && typeof select.searchEl.focus === 'function') {
                select.searchEl.focus()
            }
            select.open = true
        },
        translate(key, fallback) {
            const translation = this.$t(key)
            return translation && translation !== key ? translation : fallback
        },
        productIdLabel(product) {
            const id = productId(product)
            return id ? `Product #${id}` : ''
        },
        displayLabel(product) {
            if (!product) {
                return ''
            }

            const primary = product.name || product.identifier || this.productIdLabel(product)
            return product.name && product.identifier
                ? `${product.name} · ${product.identifier}`
                : primary
        },
        noOptionsLabel(search) {
            if (!this.lookup.available) {
                return this.translate(
                    'Component(ResearchReviewProcess):product-lookup-unavailable',
                    'Product search is not available in this review context.',
                )
            }

            return search && search.trim().length >= this.minChars
                ? this.translate('Component(Text):nothing-found', 'Nothing found')
                : this.translate(
                    'Component(ResearchReviewProcess):start-product-search',
                    'Enter at least two characters to search.',
                )
        },
        search(query, loading) {
            clearTimeout(this.searchTimer)
            const normalizedQuery = typeof query === 'string' ? query.trim() : ''
            this.searchRevision += 1
            const revision = this.searchRevision

            if (!this.lookup.available || normalizedQuery.length < this.minChars) {
                this.options = []
                loading(false)
                return
            }

            loading(true)
            this.searchTimer = setTimeout(() => {
                this.lookup.search(normalizedQuery)
                    .then(items => {
                        if (revision !== this.searchRevision) {
                            return
                        }

                        const excludedIds = this.excludedProductIds.map(String)
                        this.options = items
                            .filter(item => !excludedIds.includes(String(productId(item))))
                            .map(item => ({
                                ...item,
                                id: String(productId(item)),
                                displayLabel: this.displayLabel(item),
                            }))
                    })
                    .catch(() => {
                        if (revision === this.searchRevision) {
                            this.options = []
                        }
                    })
                    .finally(() => {
                        if (revision === this.searchRevision) {
                            loading(false)
                        }
                    })
            }, this.debounceMs)
        },
    },
}
</script>
